/**
 * Geofence Picker Component
 * Allows selecting a center location and displays a radius circle for geofencing
 * Also supports polygon drawing mode for polygon-based geofences
 */

import React, { useEffect, useRef, useCallback, useMemo } from 'react'
import { Marker, Circle, Polygon, useMapEvents, useMap, FeatureGroup, Polyline, Popup } from 'react-leaflet'
import { Typography } from '@mui/material'
import { EditControl } from 'react-leaflet-draw'
import { MapContainer } from './MapContainer'
import { Box, IconButton, Tooltip } from '@mui/material'
import { MyLocation } from '@mui/icons-material'
import L from 'leaflet'
import type { FeatureGroup as LeafletFeatureGroup } from 'leaflet'
import 'leaflet-draw/dist/leaflet.draw.css'

// Create a custom DivIcon for polygon vertex markers (doesn't need image files)
const VertexIcon = new L.DivIcon({
  iconSize: new L.Point(8, 8),
  className: 'leaflet-div-icon leaflet-editing-icon',
})

const TouchVertexIcon = new L.DivIcon({
  iconSize: new L.Point(20, 20),
  className: 'leaflet-div-icon leaflet-editing-icon leaflet-touch-icon',
})

interface GeofencePickerProps {
  /** Mode: 'circle' for radius-based, 'polygon' for polygon-based geofence */
  mode?: 'circle' | 'polygon'
  /** Center point for circle mode */
  center?: { latitude: number; longitude: number }
  /** Radius in miles for circle mode */
  radiusMiles?: number
  /** Callback when center location changes (circle mode) */
  onChange?: (location: { latitude: number; longitude: number }) => void
  /** Polygon coordinates in [lng, lat] format (polygon mode) */
  polygonCoordinates?: [number, number][]
  /** Callback when polygon changes (polygon mode) */
  onPolygonChange?: (coords: [number, number][]) => void
  height?: string | number
  showRadius?: boolean
  radiusColor?: string
  radiusLabel?: string
  /** Optional context markers to show other POI locations (faded background) */
  contextMarkers?: ContextMarker[]
  /** Optional context routes to show route polylines (faded background) */
  contextRoutes?: ContextRoute[]
}

function MapClickHandler({ onLocationSelect, enabled }: { onLocationSelect: (lat: number, lng: number) => void; enabled: boolean }) {
  useMapEvents({
    click: (e) => {
      if (enabled) {
        onLocationSelect(e.latlng.lat, e.latlng.lng)
      }
    },
  })
  return null
}

// Component to handle map centering when location changes
function MapCenterController({ center }: { center?: { latitude: number; longitude: number } }) {
  const map = useMap()

  useEffect(() => {
    if (center) {
      const currentZoom = map.getZoom()
      map.setView([center.latitude, center.longitude], currentZoom, {
        animate: true,
      })
    }
  }, [center?.latitude, center?.longitude, map])

  return null
}

// Component to fit map to polygon bounds
function PolygonBoundsController({ coordinates }: { coordinates?: [number, number][] }) {
  const map = useMap()

  useEffect(() => {
    if (coordinates && coordinates.length >= 3) {
      // Convert [lng, lat] to [lat, lng] for Leaflet
      const latLngs = coordinates.map(([lng, lat]) => [lat, lng] as [number, number])
      const bounds = latLngs.reduce<L.LatLngBoundsLiteral>(
        (acc, [lat, lng]) => {
          return [
            [Math.min(acc[0][0], lat), Math.min(acc[0][1], lng)],
            [Math.max(acc[1][0], lat), Math.max(acc[1][1], lng)],
          ] as L.LatLngBoundsLiteral
        },
        [[90, 180], [-90, -180]] as L.LatLngBoundsLiteral
      )
      map.fitBounds(bounds, { padding: [50, 50] })
    }
  }, [coordinates, map])

  return null
}

// Convert miles to meters for Leaflet Circle
const milesToMeters = (miles: number) => miles * 1609.34

// Context marker type for showing other POI locations
export interface ContextMarker {
  id: string
  label: string
  latitude: number
  longitude: number
  emoji: string
  markerStyle: 'icon' | 'circle' | 'none'
  circleRadius?: number
  circleColor?: string
}

// Context route type for showing route polylines
export interface ContextRoute {
  id: string
  coordinates: [number, number][]
  color: string
  style: 'solid' | 'dashed' | 'dotted'
  opacity: number
  arrowStyle: 'none' | 'end' | 'along'
}

// Create emoji-based marker icon (faded for context)
const createContextEmojiIcon = (emoji: string) => {
  return new L.DivIcon({
    className: 'context-emoji-marker',
    html: `
      <div style="
        font-size: 20px;
        text-shadow: 0 1px 2px rgba(0,0,0,0.2);
        line-height: 1;
        opacity: 0.5;
      ">
        ${emoji}
      </div>
    `,
    iconSize: [20, 20],
    iconAnchor: [10, 10],
    popupAnchor: [0, -10],
  })
}

// Hidden location marker icon (ghosted red X for editor preview)
const createHiddenLocationIcon = () => {
  return new L.DivIcon({
    className: 'hidden-location-marker',
    html: `
      <div style="
        font-size: 16px;
        color: #ef5350;
        opacity: 0.4;
        text-shadow: 0 1px 2px rgba(0,0,0,0.2);
        line-height: 1;
      ">✕</div>
    `,
    iconSize: [16, 16],
    iconAnchor: [8, 8],
    popupAnchor: [0, -8],
  })
}

// Create arrow icon for route direction
const createRouteArrowIcon = (color: string, rotation: number) => {
  return new L.DivIcon({
    className: 'route-arrow-marker',
    html: `
      <div style="
        transform: rotate(${rotation}deg);
        color: ${color};
        font-size: 12px;
        line-height: 1;
        text-shadow: 0 1px 2px rgba(0,0,0,0.3);
        opacity: 0.5;
      ">▶</div>
    `,
    iconSize: [12, 12],
    iconAnchor: [6, 6],
  })
}

// Calculate bearing between two points for arrow rotation
const calculateBearing = (start: [number, number], end: [number, number]): number => {
  const startLat = start[0] * Math.PI / 180
  const startLng = start[1] * Math.PI / 180
  const endLat = end[0] * Math.PI / 180
  const endLng = end[1] * Math.PI / 180

  const dLng = endLng - startLng
  const x = Math.sin(dLng) * Math.cos(endLat)
  const y = Math.cos(startLat) * Math.sin(endLat) - Math.sin(startLat) * Math.cos(endLat) * Math.cos(dLng)

  let bearing = Math.atan2(x, y) * 180 / Math.PI
  return (bearing + 360) % 360 - 90 // Adjust for arrow pointing right
}

export function GeofencePicker({
  mode = 'circle',
  center,
  radiusMiles = 0,
  onChange,
  polygonCoordinates,
  onPolygonChange,
  height = '400px',
  showRadius = true,
  radiusColor = '#1976d2',
  contextMarkers = [],
  contextRoutes = [],
}: GeofencePickerProps) {
  const featureGroupRef = useRef<LeafletFeatureGroup | null>(null)

  const handleLocationSelect = useCallback((latitude: number, longitude: number) => {
    onChange?.({ latitude, longitude })
  }, [onChange])

  const handleGetCurrentLocation = () => {
    if ('geolocation' in navigator) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          handleLocationSelect(position.coords.latitude, position.coords.longitude)
        },
        (error) => {
          alert('Failed to get your location: ' + error.message)
        }
      )
    } else {
      alert('Geolocation is not supported by your browser')
    }
  }

  // Handle polygon creation
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handlePolygonCreated = useCallback((e: any) => {
    const layer = e.layer
    const latLngs = layer.getLatLngs()[0] as { lat: number; lng: number }[]

    // Convert to [lng, lat] format and close the polygon
    const coords: [number, number][] = latLngs.map((ll) => [ll.lng, ll.lat])
    if (coords.length > 0) {
      // Ensure polygon is closed
      const first = coords[0]
      const last = coords[coords.length - 1]
      if (first[0] !== last[0] || first[1] !== last[1]) {
        coords.push([...first])
      }
    }

    onPolygonChange?.(coords)

    // Clear the drawn layer since we'll render it ourselves
    if (featureGroupRef.current) {
      featureGroupRef.current.clearLayers()
    }
  }, [onPolygonChange])

  // Handle polygon edit
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handlePolygonEdited = useCallback((e: any) => {
    e.layers.eachLayer((layer: any) => {
      const latLngs = layer.getLatLngs()[0] as { lat: number; lng: number }[]
      const coords: [number, number][] = latLngs.map((ll) => [ll.lng, ll.lat])
      if (coords.length > 0) {
        const first = coords[0]
        const last = coords[coords.length - 1]
        if (first[0] !== last[0] || first[1] !== last[1]) {
          coords.push([...first])
        }
      }
      onPolygonChange?.(coords)
    })
  }, [onPolygonChange])

  // Handle polygon delete
  const handlePolygonDeleted = useCallback(() => {
    onPolygonChange?.([])
  }, [onPolygonChange])

  // Default center to US center if no location selected
  const defaultCenter: [number, number] = [39.8283, -98.5795]
  const mapCenter: [number, number] = center
    ? [center.latitude, center.longitude]
    : defaultCenter

  // Convert polygon coordinates from [lng, lat] to [lat, lng] for Leaflet
  const leafletPolygonCoords: [number, number][] | undefined = polygonCoordinates?.map(
    ([lng, lat]) => [lat, lng] as [number, number]
  )

  const hasPolygon = polygonCoordinates && polygonCoordinates.length >= 3

  // Memoize draw configuration to prevent EditControl from resetting on re-renders
  const drawConfig = useMemo(() => ({
    rectangle: false as const,
    circle: false as const,
    circlemarker: false as const,
    marker: false as const,
    polyline: false as const,
    polygon: !hasPolygon ? {
      allowIntersection: false,
      showArea: false,
      showLength: false,
      drawError: {
        color: '#e1e100',
        message: '<strong>Error:</strong> Shape edges cannot cross!',
      },
      shapeOptions: {
        color: radiusColor,
        fillColor: radiusColor,
        fillOpacity: 0.2,
        weight: 2,
      },
      guidelineDistance: 20,
      maxGuideLineLength: 4000,
      repeatMode: false,
      icon: VertexIcon,
      touchIcon: TouchVertexIcon,
    } : false as const,
  }), [hasPolygon, radiusColor])

  // Memoize edit configuration
  const editConfig = useMemo(() => ({
    edit: false as const,
    remove: false as const,
  }), [])

  return (
    <Box sx={{ position: 'relative' }}>
      <MapContainer
        center={mapCenter}
        zoom={center ? 10 : 4}
        height={height}
      >
        {mode === 'circle' && <MapCenterController center={center} />}
        {mode === 'polygon' && hasPolygon && <PolygonBoundsController coordinates={polygonCoordinates} />}

        {/* Circle mode: click to set center */}
        <MapClickHandler onLocationSelect={handleLocationSelect} enabled={mode === 'circle'} />

        {/* Context Route Polylines (faded background) */}
        {contextRoutes.map(route => (
          <React.Fragment key={route.id}>
            <Polyline
              positions={route.coordinates}
              pathOptions={{
                color: route.color,
                weight: 3,
                opacity: route.opacity * 0.5, // Extra fading for context
                dashArray: route.style === 'dashed' ? '10, 10' :
                          route.style === 'dotted' ? '4, 8' : undefined,
              }}
            />
            {/* Arrow at end of route */}
            {route.arrowStyle === 'end' && route.coordinates.length >= 2 && (
              <Marker
                key={`${route.id}-arrow-end`}
                position={route.coordinates[route.coordinates.length - 1]}
                icon={createRouteArrowIcon(
                  route.color,
                  calculateBearing(
                    route.coordinates[route.coordinates.length - 2],
                    route.coordinates[route.coordinates.length - 1]
                  )
                )}
              />
            )}
            {/* Arrows along the path */}
            {route.arrowStyle === 'along' && route.coordinates.length >= 2 && (
              <>
                {route.coordinates
                  .filter((_, i) => i > 0 && i % Math.max(1, Math.floor(route.coordinates.length / 5)) === 0)
                  .map((coord, i) => {
                    const originalIndex = route.coordinates.indexOf(coord)
                    const prevCoord = route.coordinates[originalIndex - 1]
                    return (
                      <Marker
                        key={`${route.id}-arrow-${i}`}
                        position={coord}
                        icon={createRouteArrowIcon(route.color, calculateBearing(prevCoord, coord))}
                      />
                    )
                  })}
                <Marker
                  key={`${route.id}-arrow-final`}
                  position={route.coordinates[route.coordinates.length - 1]}
                  icon={createRouteArrowIcon(
                    route.color,
                    calculateBearing(
                      route.coordinates[route.coordinates.length - 2],
                      route.coordinates[route.coordinates.length - 1]
                    )
                  )}
                />
              </>
            )}
          </React.Fragment>
        ))}

        {/* Context POI Markers (faded background) */}
        {contextMarkers.map(marker => (
          marker.markerStyle === 'none' ? (
            // Hidden location - show ghosted red X
            <Marker
              key={marker.id}
              position={[marker.latitude, marker.longitude]}
              icon={createHiddenLocationIcon()}
            >
              <Popup>
                <Typography variant="body2" sx={{ color: '#ef5350' }}>
                  {marker.label} (Hidden)
                </Typography>
              </Popup>
            </Marker>
          ) : marker.markerStyle === 'circle' ? (
            // Circle marker
            <Circle
              key={marker.id}
              center={[marker.latitude, marker.longitude]}
              radius={marker.circleRadius || 50}
              pathOptions={{
                color: marker.circleColor || '#2196F3',
                fillColor: marker.circleColor || '#2196F3',
                fillOpacity: 0.1,
                weight: 1,
                opacity: 0.5,
              }}
            >
              <Popup>
                <Typography variant="body2">{marker.label}</Typography>
              </Popup>
            </Circle>
          ) : (
            // Icon marker
            <Marker
              key={marker.id}
              position={[marker.latitude, marker.longitude]}
              icon={createContextEmojiIcon(marker.emoji)}
            >
              <Popup>
                <Typography variant="body2">{marker.emoji} {marker.label}</Typography>
              </Popup>
            </Marker>
          )
        ))}

        {/* Circle mode rendering */}
        {mode === 'circle' && center && (
          <>
            <Marker position={[center.latitude, center.longitude]} />
            {showRadius && radiusMiles > 0 && (
              <Circle
                center={[center.latitude, center.longitude]}
                radius={milesToMeters(radiusMiles)}
                pathOptions={{
                  color: radiusColor,
                  fillColor: radiusColor,
                  fillOpacity: 0.2,
                  weight: 2,
                }}
              />
            )}
          </>
        )}

        {/* Polygon mode rendering */}
        {mode === 'polygon' && (
          <>
            <FeatureGroup key="polygon-feature-group" ref={featureGroupRef}>
              <EditControl
                key="polygon-edit-control"
                position="topright"
                onCreated={handlePolygonCreated}
                onEdited={handlePolygonEdited}
                onDeleted={handlePolygonDeleted}
                draw={drawConfig}
                edit={editConfig}
              />
            </FeatureGroup>

            {/* Render existing polygon */}
            {hasPolygon && leafletPolygonCoords && (
              <Polygon
                positions={leafletPolygonCoords}
                pathOptions={{
                  color: radiusColor,
                  fillColor: radiusColor,
                  fillOpacity: 0.2,
                  weight: 2,
                }}
              />
            )}
          </>
        )}
      </MapContainer>

      {/* Use My Location Button Overlay - only show in circle mode */}
      {mode === 'circle' && (
        <Tooltip title="Use My Current Location" placement="left">
          <IconButton
            onClick={handleGetCurrentLocation}
            sx={{
              position: 'absolute',
              top: 10,
              right: 10,
              backgroundColor: 'background.paper',
              boxShadow: 2,
              '&:hover': {
                backgroundColor: 'background.paper',
                boxShadow: 4,
              },
              zIndex: 1000,
            }}
          >
            <MyLocation />
          </IconButton>
        </Tooltip>
      )}
    </Box>
  )
}
